<script setup>
import { ref, computed, watch, onMounted } from 'vue';

const emit = defineEmits(['select', 'close']);

const search = ref('');
const searchInput = ref(null);
const discounts = ref([]);
const loading = ref(false);
const error = ref('');
const filter = ref('all');
let debounceTimer = null;

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'percent', label: '% off' },
  { key: 'amount', label: '$ off' },
];

const fetchDiscounts = async () => {
  loading.value = true;
  error.value = '';
  try {
    const res = await fetch(
      `/agent-discounts?q=${encodeURIComponent(search.value.trim())}`
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    discounts.value = data.discounts || [];
  } catch (e) {
    error.value = 'Could not load discounts. Please try again.';
    discounts.value = [];
  } finally {
    loading.value = false;
  }
};

watch(search, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fetchDiscounts, 300);
});

onMounted(() => {
  fetchDiscounts();
  searchInput.value?.focus();
});

// Biggest value first. Percents and amounts are different units, so "All"
// keeps them as two blocks (percents, then amounts), each sorted descending;
// discounts with no structured value sink to the bottom alphabetically.
const groupRank = d =>
  ({ percent: 0, amount: 1 }[d.value_type] ?? 2);

const visibleDiscounts = computed(() => {
  const list =
    filter.value === 'all'
      ? discounts.value
      : discounts.value.filter(d => d.value_type === filter.value);
  return [...list].sort((a, b) => {
    const rank = groupRank(a) - groupRank(b);
    if (rank) return rank;
    const val = (b.value || 0) - (a.value || 0);
    if (val) return val;
    return (a.code || '').localeCompare(b.code || '');
  });
});

const countFor = key =>
  key === 'all'
    ? discounts.value.length
    : discounts.value.filter(d => d.value_type === key).length;

const money = d => {
  const n = Number(d.value || 0);
  const s = Number.isInteger(n) ? n : n.toFixed(2);
  return d.currency && d.currency !== 'USD' ? `${s} ${d.currency}` : `$${s}`;
};

const badge = d => {
  if (d.value_type === 'percent')
    return `${Number.isInteger(d.value) ? d.value : d.value.toFixed(1)}%`;
  if (d.value_type === 'amount') return money(d);
  if (d.value_type === 'shipping') return 'SHIP';
  return '—';
};

const usageLabel = d => {
  if (!d.usage_limit) return '';
  return `${Math.max(d.usage_limit - (d.usage_count || 0), 0)} of ${d.usage_limit} left`;
};

const endsLabel = d => {
  if (!d.ends_at) return '';
  const dt = new Date(d.ends_at);
  return Number.isNaN(dt.getTime())
    ? ''
    : `ends ${dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
};

const onSelect = d => emit('select', d);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
    @click.self="emit('close')"
  >
    <div
      class="flex flex-col w-[34rem] max-w-[92vw] max-h-[80vh] overflow-hidden border rounded-xl shadow-xl bg-n-background border-n-weak"
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          Send a discount card
        </h3>
        <button
          class="flex text-n-slate-11 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <span class="i-ph-x text-lg" />
        </button>
      </div>
      <div class="flex items-center gap-2 p-3 border-b border-n-weak">
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          placeholder="Search by code..."
          class="flex-1 px-3 py-2 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-brand"
        />
        <div class="flex gap-1">
          <button
            v-for="f in FILTERS"
            :key="f.key"
            class="px-2.5 py-1.5 text-xs font-medium border rounded-full whitespace-nowrap"
            :class="
              filter === f.key
                ? 'border-n-brand text-n-brand bg-n-brand/10'
                : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
            "
            @click="filter = f.key"
          >
            {{ f.label }} ({{ countFor(f.key) }})
          </button>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto">
        <div v-if="loading" class="p-6 text-sm text-center text-n-slate-11">
          Loading...
        </div>
        <div v-else-if="error" class="p-6 text-sm text-center text-n-ruby-11">
          {{ error }}
        </div>
        <div
          v-else-if="!visibleDiscounts.length"
          class="p-6 text-sm text-center text-n-slate-11"
        >
          No active discounts found.
        </div>
        <button
          v-for="d in visibleDiscounts"
          :key="d.code"
          class="flex items-center w-full gap-3 px-4 py-2.5 text-left border-b border-n-weak hover:bg-n-alpha-1"
          @click="onSelect(d)"
        >
          <span
            class="flex items-center justify-center flex-shrink-0 h-10 px-2 min-w-14 text-sm font-bold rounded-md bg-n-brand/10 text-n-brand"
          >
            {{ badge(d) }}
          </span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-mono truncate text-n-slate-12">
              {{ d.code }}
            </div>
            <div class="text-xs truncate text-n-slate-11">
              <span v-if="d.summary">{{ d.summary }}</span>
              <span v-if="d.automatic"> (automatic)</span>
            </div>
          </div>
          <div
            class="flex flex-col items-end flex-shrink-0 gap-0.5 text-[11px] text-n-slate-10"
          >
            <span v-if="usageLabel(d)">{{ usageLabel(d) }}</span>
            <span v-if="endsLabel(d)">{{ endsLabel(d) }}</span>
          </div>
          <span class="flex-shrink-0 i-ph-paper-plane-tilt text-n-slate-10" />
        </button>
      </div>
    </div>
  </div>
</template>
